import { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Wrench, FileText, Briefcase, Library, Search, Upload, Plus, Folder,
  FolderOpen, ChevronRight, ChevronDown, Pencil, Trash2, File,
  FileImage, X, Download, Loader2, Eye, AlertTriangle,
} from "lucide-react";
import type { ProcedureCategorie, ProcedureDocument } from "@shared/schema";
import { SERVICES_PROCEDURES } from "@shared/schema";

const SERVICE_ICONS: Record<string, any> = { Wrench, FileText, Briefcase };

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function fileIcon(type: string | null | undefined) {
  if (!type) return <File className="w-4 h-4 text-muted-foreground" />;
  if (type.startsWith("image/")) return <FileImage className="w-4 h-4 text-blue-500" />;
  if (type === "application/pdf") return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-muted-foreground" />;
}

type TreeNode = ProcedureCategorie & { children: TreeNode[] };

function buildTree(cats: ProcedureCategorie[], parentId: string | null = null): TreeNode[] {
  return cats
    .filter(c => (c.parentId ?? null) === parentId)
    .map(c => ({ ...c, children: buildTree(cats, c.id) }))
    .sort((a, b) => a.ordre - b.ordre || a.nom.localeCompare(b.nom));
}

function CatNode({
  node,
  selectedId,
  onSelect,
  peutGerer,
  onRename,
  onDelete,
  docCount,
}: {
  node: TreeNode;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  peutGerer: boolean;
  onRename: (cat: ProcedureCategorie) => void;
  onDelete: (cat: ProcedureCategorie) => void;
  docCount: (id: string) => number;
}) {
  const [open, setOpen] = useState(false);
  const hasChildren = node.children.length > 0;
  const isActive = selectedId === node.id;
  const count = docCount(node.id);

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer group text-sm transition-colors",
          isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/60 text-foreground"
        )}
      >
        <button
          className="shrink-0 w-5 h-5 flex items-center justify-center text-muted-foreground"
          onClick={e => { e.stopPropagation(); if (hasChildren) setOpen(o => !o); }}
        >
          {hasChildren ? (open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="w-3.5" />}
        </button>
        <button className="flex-1 flex items-center gap-2 min-w-0 text-left" onClick={() => { onSelect(node.id); if (hasChildren) setOpen(o => !o); }}>
          {open ? <FolderOpen className="w-4 h-4 shrink-0 text-amber-500" /> : <Folder className="w-4 h-4 shrink-0 text-amber-400" />}
          <span className="truncate">{node.nom}</span>
          {count > 0 && <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{count}</span>}
        </button>
        {peutGerer && (
          <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
            <button
              className="p-1 rounded hover:bg-muted"
              onClick={e => { e.stopPropagation(); onRename(node); }}
              title="Renommer"
            ><Pencil className="w-3 h-3 text-muted-foreground" /></button>
            <button
              className="p-1 rounded hover:bg-destructive/10 text-destructive"
              onClick={e => { e.stopPropagation(); onDelete(node); }}
              title="Supprimer"
            ><Trash2 className="w-3 h-3" /></button>
          </div>
        )}
      </div>
      {open && hasChildren && (
        <div className="ml-5 border-l border-border/50 pl-1 mt-0.5 space-y-0.5">
          {node.children.map(child => (
            <CatNode key={child.id} node={child} selectedId={selectedId} onSelect={onSelect}
              peutGerer={peutGerer} onRename={onRename} onDelete={onDelete} docCount={docCount} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProceduresPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [service, setService] = useState<string>(SERVICES_PROCEDURES[0].slug);
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [folderDialog, setFolderDialog] = useState<{ open: boolean; cat?: ProcedureCategorie; parentId?: string | null }>({ open: false });
  const [folderName, setFolderName] = useState("");
  const [folderDesc, setFolderDesc] = useState("");

  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [editDocDialog, setEditDocDialog] = useState<{ open: boolean; doc?: ProcedureDocument }>({ open: false });
  const [editDocTitle, setEditDocTitle] = useState("");
  const [editDocDesc, setEditDocDesc] = useState("");

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<ProcedureDocument | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: droits } = useQuery<{ peutGerer: boolean }>({
    queryKey: ["/api/procedures/droits"],
    queryFn: async () => (await authFetch("/api/procedures/droits")).json(),
  });
  const peutGerer = droits?.peutGerer ?? false;

  const { data: cats = [] } = useQuery<ProcedureCategorie[]>({
    queryKey: ["/api/procedures/categories", service],
    queryFn: async () => (await authFetch(`/api/procedures/categories?service=${service}`)).json(),
  });

  const { data: allDocs = [], isLoading: docsLoading } = useQuery<ProcedureDocument[]>({
    queryKey: ["/api/procedures/documents/all", service],
    queryFn: async () => (await authFetch(`/api/procedures/documents/all?service=${service}`)).json(),
  });

  const tree = useMemo(() => buildTree(cats, null), [cats]);

  const docCount = (catId: string) => allDocs.filter(d => d.categorieId === catId).length;

  const displayedDocs = useMemo(() => {
    let docs = allDocs;
    if (search.trim()) {
      const q = search.toLowerCase();
      docs = docs.filter(d => d.titre.toLowerCase().includes(q) || (d.description || "").toLowerCase().includes(q));
    } else if (selectedCatId) {
      docs = docs.filter(d => d.categorieId === selectedCatId);
    }
    return docs;
  }, [allDocs, selectedCatId, search]);

  const createCatMutation = useMutation({
    mutationFn: async (data: { service: string; parentId?: string | null; nom: string; description?: string }) => {
      const res = await authFetch("/api/procedures/categories", { method: "POST", body: JSON.stringify(data) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/procedures/categories", service] });
      setFolderDialog({ open: false });
      toast({ title: "Dossier créé" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const renameCatMutation = useMutation({
    mutationFn: async ({ id, nom, description }: { id: string; nom: string; description?: string }) => {
      const res = await authFetch(`/api/procedures/categories/${id}`, { method: "PATCH", body: JSON.stringify({ nom, description }) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/procedures/categories", service] });
      setFolderDialog({ open: false });
      toast({ title: "Dossier renommé" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const deleteCatMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/procedures/categories/${id}`, { method: "DELETE" });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/procedures/categories", service] });
      if (selectedCatId) setSelectedCatId(null);
      toast({ title: "Dossier supprimé" });
    },
    onError: (e: any) => toast({ title: "Impossible de supprimer", description: e.message, variant: "destructive" }),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error("Aucun fichier sélectionné");
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("service", service);
      fd.append("titre", uploadTitle.trim());
      if (uploadDesc) fd.append("description", uploadDesc.trim());
      if (selectedCatId) fd.append("categorieId", selectedCatId);
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token || "";
      const res = await fetch("/api/procedures/documents", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/procedures/documents/all", service] });
      setUploadDialog(false);
      setUploadTitle(""); setUploadDesc(""); setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast({ title: "Document uploadé" });
    },
    onError: (e: any) => toast({ title: "Erreur upload", description: e.message, variant: "destructive" }),
  });

  const editDocMutation = useMutation({
    mutationFn: async ({ id, titre, description }: { id: string; titre: string; description?: string }) => {
      const res = await authFetch(`/api/procedures/documents/${id}`, { method: "PATCH", body: JSON.stringify({ titre, description }) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/procedures/documents/all", service] });
      setEditDocDialog({ open: false });
      toast({ title: "Document mis à jour" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/procedures/documents/${id}`, { method: "DELETE" });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/procedures/documents/all", service] });
      toast({ title: "Document supprimé" });
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const getUrl = async (doc: ProcedureDocument): Promise<string | null> => {
    const res = await authFetch(`/api/procedures/documents/${doc.id}/url`);
    if (!res.ok) return null;
    const { url } = await res.json();
    return url;
  };

  const handlePreview = async (doc: ProcedureDocument) => {
    setLoadingPreviewId(doc.id);
    const url = await getUrl(doc);
    setLoadingPreviewId(null);
    if (!url) { toast({ title: "Erreur", description: "URL introuvable", variant: "destructive" }); return; }
    setPreviewDoc(doc);
    setPreviewUrl(url);
  };

  const handleDownload = async (doc: ProcedureDocument) => {
    setDownloadingId(doc.id);
    const url = await getUrl(doc);
    setDownloadingId(null);
    if (!url) { toast({ title: "Erreur", description: "URL introuvable", variant: "destructive" }); return; }
    window.open(url, "_blank");
  };

  const openFolderCreate = (parentId: string | null = null) => {
    setFolderName(""); setFolderDesc("");
    setFolderDialog({ open: true, parentId });
  };
  const openFolderRename = (cat: ProcedureCategorie) => {
    setFolderName(cat.nom); setFolderDesc(cat.description || "");
    setFolderDialog({ open: true, cat });
  };
  const handleFolderSubmit = () => {
    if (!folderName.trim()) return;
    if (folderDialog.cat) {
      renameCatMutation.mutate({ id: folderDialog.cat.id, nom: folderName, description: folderDesc });
    } else {
      createCatMutation.mutate({ service, parentId: folderDialog.parentId ?? null, nom: folderName, description: folderDesc });
    }
  };

  const openUpload = () => {
    setUploadTitle(""); setUploadDesc(""); setUploadFile(null);
    setUploadDialog(true);
  };

  const currentCatName = selectedCatId ? cats.find(c => c.id === selectedCatId)?.nom : null;

  return (
    <div className="flex flex-col h-full min-h-0 space-y-0">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b bg-background">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Library className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Procédures</h1>
              <p className="text-sm text-muted-foreground">Bibliothèque documentaire interne</p>
            </div>
          </div>
        </div>
        {/* Service tabs */}
        <div className="flex gap-1">
          {SERVICES_PROCEDURES.map(svc => {
            const Icon = SERVICE_ICONS[svc.icon] || FileText;
            return (
              <button
                key={svc.slug}
                onClick={() => { setService(svc.slug); setSelectedCatId(null); setSearch(""); }}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                  service === svc.slug
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                )}
              >
                <Icon className="w-4 h-4" />
                {svc.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: category tree */}
        <div className="w-64 shrink-0 border-r bg-muted/20 flex flex-col overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Dossiers</span>
            {peutGerer && (
              <button
                onClick={() => openFolderCreate(selectedCatId)}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title="Nouveau dossier"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {/* Root entry */}
            <button
              onClick={() => setSelectedCatId(null)}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors",
                !selectedCatId ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/60 text-foreground"
              )}
            >
              <FolderOpen className="w-4 h-4 text-primary/70" />
              <span>Tous les documents</span>
              <span className="ml-auto text-[10px] text-muted-foreground">{allDocs.length}</span>
            </button>
            {tree.length === 0 && (
              <p className="text-xs text-muted-foreground px-3 py-2">Aucun dossier dans ce service</p>
            )}
            {tree.map(node => (
              <CatNode
                key={node.id}
                node={node}
                selectedId={selectedCatId}
                onSelect={setSelectedCatId}
                peutGerer={peutGerer}
                onRename={openFolderRename}
                onDelete={cat => { if (window.confirm(`Supprimer le dossier « ${cat.nom} » ?`)) deleteCatMutation.mutate(cat.id); }}
                docCount={docCount}
              />
            ))}
            {tree.length === 0 && peutGerer && (
              <div className="px-2 pt-1">
                <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => openFolderCreate(null)}>
                  <Plus className="w-3 h-3 mr-1" />Créer un dossier
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: document list */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9 h-9"
                placeholder="Rechercher un document…"
                value={search}
                onChange={e => { setSearch(e.target.value); if (e.target.value) setSelectedCatId(null); }}
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {currentCatName && !search && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Folder className="w-4 h-4 text-amber-400" />
                <span className="font-medium text-foreground">{currentCatName}</span>
              </div>
            )}
            <div className="ml-auto">
              {peutGerer && (
                <Button size="sm" onClick={openUpload} data-testid="button-upload-document">
                  <Upload className="w-4 h-4 mr-2" />
                  Uploader un document
                </Button>
              )}
            </div>
          </div>

          {/* Document list */}
          <div className="flex-1 overflow-y-auto p-4">
            {docsLoading ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />Chargement…
              </div>
            ) : displayedDocs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3 text-muted-foreground">
                <FileText className="w-10 h-10 opacity-30" />
                <p className="text-sm">{search ? "Aucun résultat" : "Aucun document"}</p>
                {peutGerer && !search && (
                  <Button size="sm" variant="outline" onClick={openUpload}>
                    <Upload className="w-4 h-4 mr-2" />Uploader un document
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {displayedDocs.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-3 rounded-xl border bg-card hover:shadow-sm transition-shadow group"
                    data-testid={`doc-item-${doc.id}`}
                  >
                    <div className="shrink-0">{fileIcon(doc.fichierType)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.titre}</p>
                      {doc.description && <p className="text-xs text-muted-foreground truncate">{doc.description}</p>}
                      <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                        <span>{doc.fichierNom}</span>
                        {doc.fichierTaille && <span>· {formatBytes(doc.fichierTaille)}</span>}
                        <span>· {new Date(doc.createdAt).toLocaleDateString("fr-FR")}</span>
                        {doc.uploadedBy && <span>· {doc.uploadedBy}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {doc.fichierType === "application/pdf" && (
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          disabled={loadingPreviewId === doc.id}
                          onClick={() => handlePreview(doc)}
                          title="Aperçu"
                        >
                          {loadingPreviewId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        disabled={downloadingId === doc.id}
                        onClick={() => handleDownload(doc)}
                        title="Télécharger"
                      >
                        {downloadingId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                      {peutGerer && (
                        <>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => { setEditDocTitle(doc.titre); setEditDocDesc(doc.description || ""); setEditDocDialog({ open: true, doc }); }}
                            title="Modifier"
                          ><Pencil className="w-4 h-4" /></Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                            onClick={() => { if (window.confirm(`Supprimer « ${doc.titre} » ?`)) deleteDocMutation.mutate(doc.id); }}
                            title="Supprimer"
                          ><Trash2 className="w-4 h-4" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Folder dialog */}
      <Dialog open={folderDialog.open} onOpenChange={o => setFolderDialog(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{folderDialog.cat ? "Renommer le dossier" : "Nouveau dossier"}</DialogTitle>
            <DialogDescription>
              {folderDialog.cat ? `Modification de « ${folderDialog.cat.nom} »` : "Créer un nouveau dossier dans ce service"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label>Nom *</Label>
              <Input value={folderName} onChange={e => setFolderName(e.target.value)} placeholder="ex : Protocoles d'installation" autoFocus />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={folderDesc} onChange={e => setFolderDesc(e.target.value)} placeholder="Optionnel" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setFolderDialog({ open: false })}>Annuler</Button>
              <Button
                onClick={handleFolderSubmit}
                disabled={!folderName.trim() || createCatMutation.isPending || renameCatMutation.isPending}
              >
                {(createCatMutation.isPending || renameCatMutation.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {folderDialog.cat ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Uploader un document</DialogTitle>
            <DialogDescription>
              {currentCatName ? `Vers le dossier « ${currentCatName} »` : "Ajouté à la racine du service"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label>Titre *</Label>
              <Input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="ex : Procédure d'installation BellaBot" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} rows={2} placeholder="Optionnel" />
            </div>
            <div className="space-y-1">
              <Label>Fichier *</Label>
              <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadFile ? (
                  <div className="flex items-center gap-2 justify-center text-sm">
                    {fileIcon(uploadFile.type)}
                    <span className="font-medium truncate max-w-xs">{uploadFile.name}</span>
                    <span className="text-muted-foreground">({formatBytes(uploadFile.size)})</span>
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    <Upload className="w-6 h-6 mx-auto mb-1 opacity-50" />
                    Cliquez pour sélectionner un fichier
                    <p className="text-xs mt-1">PDF, Word, PNG, JPG — max 50 Mo</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={e => {
                  const f = e.target.files?.[0] || null;
                  setUploadFile(f);
                  if (f && !uploadTitle) setUploadTitle(f.name.replace(/\.[^.]+$/, ""));
                }}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setUploadDialog(false)}>Annuler</Button>
              <Button
                onClick={() => uploadMutation.mutate()}
                disabled={!uploadFile || !uploadTitle.trim() || uploadMutation.isPending}
              >
                {uploadMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Upload…</> : <><Upload className="w-4 h-4 mr-2" />Uploader</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit doc dialog */}
      <Dialog open={editDocDialog.open} onOpenChange={o => setEditDocDialog(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Modifier le document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label>Titre *</Label>
              <Input value={editDocTitle} onChange={e => setEditDocTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={editDocDesc} onChange={e => setEditDocDesc(e.target.value)} rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditDocDialog({ open: false })}>Annuler</Button>
              <Button
                onClick={() => editDocMutation.mutate({ id: editDocDialog.doc!.id, titre: editDocTitle, description: editDocDesc })}
                disabled={!editDocTitle.trim() || editDocMutation.isPending}
              >
                {editDocMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview modal */}
      {previewUrl && previewDoc && (
        <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" onClick={() => { setPreviewUrl(null); setPreviewDoc(null); }}>
          <div className="flex items-center justify-between px-4 py-3 bg-background/95 border-b" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium">{previewDoc.titre}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => window.open(previewUrl, "_blank")}>
                <Download className="w-4 h-4 mr-2" />Télécharger
              </Button>
              <Button size="icon" variant="ghost" onClick={() => { setPreviewUrl(null); setPreviewDoc(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden" onClick={e => e.stopPropagation()}>
            <iframe src={previewUrl} className="w-full h-full" title={previewDoc.titre} />
          </div>
        </div>
      )}
    </div>
  );
}
